import React, { useEffect, useRef, useState } from "react";
import InstantWinListComponent from "./instantWinList.component";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { addAlert } from "modules/notification";
import { getApiLang } from "app/feature/constants";
import instantWinApi from "app/api/instantWin";

function getCountdown(now, openAt, closeAt) {
  const isStart = now.isAfter(openAt);
  const isEnd = closeAt ? now.isAfter(closeAt) : false;
  const targetDate = !isStart ? openAt : (closeAt && !isEnd ? closeAt : null);

  if (!targetDate) {
    return { day: 0, hour: 0, min: 0, sec: 0, status: "ended" };
  }

  const seconds = Math.floor(moment.duration(targetDate.diff(now)).asSeconds());
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  return {
    day: Math.max(days, 0),
    hour: hours - days * 24,
    min: minutes - hours * 60,
    sec: seconds - minutes * 60,
    status: !isStart ? "starts_in" : !isEnd ? "ends_in" : "ended",
  };
}

function isCampaignEnded(campaign) {
  return campaign.end_at && moment().isAfter(moment.utc(campaign.end_at).local());
}

function sortCampaigns(campaigns) {
  const ongoing = [];
  const ended = [];
  for (const c of campaigns) {
    if (isCampaignEnded(c)) {
      ended.push(c);
    } else {
      ongoing.push(c);
    }
  }
  return [...ongoing, ...ended];
}

export default function InstantWinListContainer({ id, isTemplateEditor }) {
  const dispatch = useDispatch();
  const { isVisible } = useSelector((state) =>
    isTemplateEditor
      ? state.products.template.workspaceSetting.setting.components[id]
      : state.productPage.components[id]
  );
  const lang = useSelector((state) => state.constant.languages);
  const productId = useSelector((state) => state.productPage.productId);
  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);       
  const [countdowns, setCountdowns] = useState({});
  const intervalRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoIntervalRef = useRef(null);

  // Fetch campaigns 
    useEffect(() => {
    setIsLoading(true);
    instantWinApi
      .getInstantWinCardDetailList(productId) 
      .then((campaignList) => {
        const sorted = sortCampaigns(Array.isArray(campaignList) ? campaignList : []);
        setCampaigns(sorted);
      })
      .catch((error) => {
        setCampaigns([]); 
        dispatch(
          addAlert({
            severity: "error",
            message: getApiLang(lang, error?.code),
          })
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, lang, productId]);

  // Countdown ticker
  useEffect(() => {
    // clear old timer to avoid duplicates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isVisible) return;
    if (!Array.isArray(campaigns) || campaigns.length === 0) return;

    intervalRef.current = setInterval(() => {
      const now = moment();
      const next = {};
      for (const c of campaigns) {
        const openAt = moment.utc(c.join_open_at).local();
        const closeAt = c.join_close_at ? moment.utc(c.join_close_at).local() : null;
        next[c.id] = getCountdown(now, openAt, closeAt);
      }
      setCountdowns(next);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [campaigns, isVisible]);

  const total = campaigns.length;
  const handlePrevious = () => {
    if (total === 0) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : total - 1));
  };

  const handleNext = () => {
    if (total === 0) return;
    setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : 0));
  };

   useEffect(() => {
    if (!isVisible || campaigns.length <= 1) return;
    if (autoIntervalRef.current) clearInterval(autoIntervalRef.current);

    autoIntervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev < campaigns.length - 1 ? prev + 1 : 0));
    }, 5000);

    return () => {
      if (autoIntervalRef.current) clearInterval(autoIntervalRef.current);
    };
  }, [isVisible, campaigns.length]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [campaigns.length]);

  return (
    isVisible && (
      <InstantWinListComponent
        campaigns={sortCampaigns(campaigns)}
        lang={lang}
        isLoading={isLoading}
        countdowns={countdowns}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
      />
    )
  );
}